import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  cancelSettlementBatch,
  createSettlementBatch,
  fetchOrderSettlementBatches,
  fetchOrderUnsettled,
  fetchSettlementBatchDetail,
  fetchSettlementsPayableSummary,
  fetchSubscriptionSettlementBatches,
  fetchSubscriptionUnsettled,
  formatSettlementAmount,
  getCurrentCompanyUserId,
  getEffectiveRestaurantId,
  markSettlementBatchPaid,
} from './financeApi';

import './css/Inventory.css';
import './css/Menu.css';
import './css/Purchase.css';
import './css/Receivables.css';
import { confirmAction } from './ui/feedback';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function daysAgoYmd(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function apiErrorMessage(err, fallback) {
  return (
    err?.response?.data?.error
    || err?.response?.data?.message
    || err?.message
    || fallback
  );
}

function statusTone(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'success';
  if (s === 'PENDING') return 'warning';
  if (s === 'CANCELLED') return 'muted';
  return 'primary';
}

const Receivables = () => {
  const restaurantId = getEffectiveRestaurantId();
  const [fromDate, setFromDate] = useState(() => daysAgoYmd(30));
  const [toDate, setToDate] = useState(() => todayYmd());
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [subUnsettled, setSubUnsettled] = useState(null);
  const [orderUnsettled, setOrderUnsettled] = useState(null);
  const [subBatches, setSubBatches] = useState([]);
  const [orderBatches, setOrderBatches] = useState([]);
  const [listView, setListView] = useState('unsettled'); // unsettled | batches
  const [settlementKind, setSettlementKind] = useState('subscription'); // subscription | regular
  const [actionError, setActionError] = useState('');
  const [actionBusy, setActionBusy] = useState(false);
  const [batchDetail, setBatchDetail] = useState(null);
  const [markPaidForm, setMarkPaidForm] = useState({
    batchId: null,
    settlementType: 'subscription',
    transactionId: '',
    bankRrn: '',
    paymentMethod: 'BANK-TRANSFER',
  });

  const dateParams = useMemo(() => ({ fromDate, toDate }), [fromDate, toDate]);

  const loadAll = useCallback(async () => {
    if (!restaurantId) {
      setError('Select or sign in to a restaurant first.');
      setSummary(null);
      setSubUnsettled(null);
      setOrderUnsettled(null);
      setSubBatches([]);
      setOrderBatches([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [payable, subU, orderU, subB, orderB] = await Promise.all([
        fetchSettlementsPayableSummary(restaurantId, dateParams),
        fetchSubscriptionUnsettled(restaurantId, dateParams),
        fetchOrderUnsettled(restaurantId, dateParams),
        fetchSubscriptionSettlementBatches(restaurantId),
        fetchOrderSettlementBatches(restaurantId),
      ]);
      setSummary(payable);
      setSubUnsettled(subU);
      setOrderUnsettled(orderU);
      setSubBatches(Array.isArray(subB?.batches) ? subB.batches : []);
      setOrderBatches(Array.isArray(orderB?.batches) ? orderB.batches : []);
    } catch (err) {
      console.error('Failed to load receivables:', err?.response?.data ?? err);
      setError(apiErrorMessage(err, 'Could not load receivables.'));
    } finally {
      setLoading(false);
    }
  }, [restaurantId, dateParams]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const unsettledRows = useMemo(() => {
    if (settlementKind === 'regular') {
      return Array.isArray(orderUnsettled?.payments) ? orderUnsettled.payments : [];
    }
    return Array.isArray(subUnsettled?.orders) ? subUnsettled.orders : [];
  }, [settlementKind, subUnsettled, orderUnsettled]);

  const batchRows = useMemo(() => {
    const rows = settlementKind === 'regular' ? orderBatches : subBatches;
    return rows.map((b) => ({ ...b, _type: settlementKind }));
  }, [settlementKind, subBatches, orderBatches]);

  const handleCreateBatch = async () => {
    const userId = getCurrentCompanyUserId();
    if (!userId) {
      setActionError('User ID not found. Sign in again.');
      return;
    }
    if (!restaurantId) return;
    const ok = await confirmAction({
      title: 'Create settlement batch?',
      message: `Create a ${settlementKind === 'regular' ? 'regular order' : 'subscription credit'} settlement batch for ${fromDate} to ${toDate}?`,
      confirmLabel: 'Create batch',
    });
    if (!ok) return;
    setActionBusy(true);
    setActionError('');
    try {
      await createSettlementBatch(restaurantId, settlementKind, {
        createdById: userId,
        fromDate,
        toDate,
      });
      await loadAll();
      setListView('batches');
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not create settlement batch.'));
    } finally {
      setActionBusy(false);
    }
  };

  const openBatchDetail = async (batchId, settlementType) => {
    setActionError('');
    try {
      const detail = await fetchSettlementBatchDetail(batchId, settlementType);
      setBatchDetail({ ...detail, _type: settlementType });
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not load batch detail.'));
    }
  };

  const openMarkPaid = (batch) => {
    setMarkPaidForm({
      batchId: batch.batchId,
      settlementType: batch._type || settlementKind,
      transactionId: '',
      bankRrn: '',
      paymentMethod: 'BANK-TRANSFER',
    });
  };

  const submitMarkPaid = async () => {
    const userId = getCurrentCompanyUserId();
    if (!userId) {
      setActionError('User ID not found. Sign in again.');
      return;
    }
    if (!markPaidForm.batchId) return;
    setActionBusy(true);
    setActionError('');
    try {
      await markSettlementBatchPaid(markPaidForm.batchId, markPaidForm.settlementType, {
        updatedById: userId,
        transactionId: markPaidForm.transactionId || undefined,
        bankRrn: markPaidForm.bankRrn || undefined,
        paymentMethod: markPaidForm.paymentMethod || 'BANK-TRANSFER',
      });
      setMarkPaidForm((prev) => ({ ...prev, batchId: null }));
      setBatchDetail(null);
      await loadAll();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not mark batch as paid.'));
    } finally {
      setActionBusy(false);
    }
  };

  const handleCancelBatch = async (batch) => {
    const userId = getCurrentCompanyUserId();
    if (!userId) {
      setActionError('User ID not found. Sign in again.');
      return;
    }
    const ok = await confirmAction({
      title: 'Cancel batch?',
      message: `Cancel pending batch #${batch.batchId}? Usages will return to unsettled.`,
      confirmLabel: 'Cancel batch',
      danger: true,
    });
    if (!ok) return;
    setActionBusy(true);
    setActionError('');
    try {
      await cancelSettlementBatch(batch.batchId, batch._type || settlementKind, {
        updatedById: userId,
      });
      setBatchDetail(null);
      await loadAll();
    } catch (err) {
      setActionError(apiErrorMessage(err, 'Could not cancel batch.'));
    } finally {
      setActionBusy(false);
    }
  };

  const subAmount = summary?.subscription?.amount ?? '0';
  const regularAmount = summary?.regular?.amount ?? '0';
  const combinedAmount = summary?.combined?.amount ?? '0';
  const subOrderCount = summary?.subscription?.orderCount ?? 0;
  const regularOrderCount = summary?.regular?.orderCount ?? 0;

  return (
    <div className="menu inventory-page purchase-page receivables-page">
      <div className="sales-header payments-header">
        <div className="sales-header__title-block">
          <h2 className="sales-heading">Payments</h2>
          <p className="sales-subheading">
            Amounts JR Eats owes this kitchen — subscription credits and regular order settlements.
          </p>
        </div>
        <div className="sales-header__actions">
          <button
            type="button"
            className="purchase-btn purchase-btn--secondary"
            onClick={() => setShowDateFilters((open) => !open)}
            aria-expanded={showDateFilters}
          >
            {showDateFilters ? 'Hide date range' : 'Date range'}
          </button>
          <button
            type="button"
            className="purchase-btn purchase-btn--secondary"
            onClick={loadAll}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            type="button"
            className="purchase-btn purchase-btn--create"
            onClick={handleCreateBatch}
            disabled={loading || actionBusy || !restaurantId}
          >
            Create {settlementKind === 'regular' ? 'regular' : 'subscription'} batch
          </button>
        </div>
      </div>

      {showDateFilters && (
        <div className="menu-toolbar-row receivables-toolbar-row receivables-date-filters payments-date-bar">
          <label className="receivables-date-field">
            <span>From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label className="receivables-date-field">
            <span>To</span>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
        </div>
      )}

      <div className="purchase-stats-grid sales-stats-grid payments-stats-grid">
        <div className="purchase-stat-card purchase-stat-card--summary sales-stat-card sales-stat-card--primary">
          <span className="sales-stat-label">Subscription</span>
          <span className="sales-stat-value purchase-stat-value--primary">
            {formatSettlementAmount(subAmount)}
          </span>
          <span className="payments-stat-meta">{subOrderCount} orders</span>
        </div>
        <div className="purchase-stat-card purchase-stat-card--summary sales-stat-card sales-stat-card--warning">
          <span className="sales-stat-label">Regular</span>
          <span className="sales-stat-value purchase-stat-value--warning">
            {formatSettlementAmount(regularAmount)}
          </span>
          <span className="payments-stat-meta">{regularOrderCount} orders</span>
        </div>
        <div className="purchase-stat-card purchase-stat-card--summary sales-stat-card sales-stat-card--success">
          <span className="sales-stat-label">Combined unsettled</span>
          <span className="sales-stat-value purchase-stat-value--success">
            {formatSettlementAmount(combinedAmount)}
          </span>
          <span className="payments-stat-meta">
            {subOrderCount + regularOrderCount} orders total
          </span>
        </div>
      </div>

      <div className="purchase-content-card">
        <div className="payments-section-bar">
          <div className="payments-section-group" role="tablist" aria-label="Settlement list">
            <span className="payments-section-label">List</span>
            <div className="payments-segment">
              <button
                type="button"
                role="tab"
                className={`payments-segment__btn ${listView === 'unsettled' ? 'is-active' : ''}`}
                aria-selected={listView === 'unsettled'}
                onClick={() => setListView('unsettled')}
              >
                Unsettled
              </button>
              <button
                type="button"
                role="tab"
                className={`payments-segment__btn ${listView === 'batches' ? 'is-active' : ''}`}
                aria-selected={listView === 'batches'}
                onClick={() => setListView('batches')}
              >
                Batches
              </button>
            </div>
          </div>

          <div className="payments-section-group" role="tablist" aria-label="Settlement type">
            <span className="payments-section-label">Type</span>
            <div className="payments-segment">
              <button
                type="button"
                className={`payments-segment__btn ${settlementKind === 'subscription' ? 'is-active' : ''}`}
                onClick={() => setSettlementKind('subscription')}
              >
                Subscription
              </button>
              <button
                type="button"
                className={`payments-segment__btn ${settlementKind === 'regular' ? 'is-active' : ''}`}
                onClick={() => setSettlementKind('regular')}
              >
                Regular
              </button>
            </div>
          </div>
        </div>

        {(error || actionError) && (
          <div className="receivables-alert" role="alert">
            {error || actionError}
          </div>
        )}

        {loading ? (
          <p className="receivables-empty">Loading payments…</p>
        ) : listView === 'unsettled' ? (
          <div className="purchase-detail-lines-table-wrap">
            <table className="purchase-detail-lines-table receivables-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th className="purchase-detail-lines-table__num">
                    {settlementKind === 'regular' ? 'Payment' : 'Credits'}
                  </th>
                  <th className="purchase-detail-lines-table__num">
                    {settlementKind === 'regular' ? 'Collected' : 'Food value'}
                  </th>
                  <th className="purchase-detail-lines-table__num">Receivable</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {unsettledRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="receivables-empty-cell">
                      No unsettled {settlementKind} items in this date range.
                    </td>
                  </tr>
                ) : (
                  unsettledRows.map((row) => (
                    <tr key={`${settlementKind}-${row.orderId}-${row.paymentId ?? row.usageId ?? ''}`}>
                      <td>#{row.orderId}</td>
                      <td>
                        {row.customerName || '—'}
                        {row.childName ? ` / ${row.childName}` : ''}
                      </td>
                      <td>{row.orderStatus || row.paymentStatus || '—'}</td>
                      <td className="purchase-detail-lines-table__num">
                        {settlementKind === 'regular'
                          ? (row.paymentId != null ? `#${row.paymentId}` : '—')
                          : (row.credits ?? '—')}
                      </td>
                      <td className="purchase-detail-lines-table__num">
                        {formatSettlementAmount(
                          settlementKind === 'regular'
                            ? (row.amount ?? row.orderTotalPrice)
                            : (row.foodValue ?? row.orderTotalPrice)
                        )}
                      </td>
                      <td className="purchase-detail-lines-table__num">
                        {formatSettlementAmount(row.payableAmount)}
                      </td>
                      <td>{row.createdDate || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="purchase-detail-lines-table-wrap">
            <table className="purchase-detail-lines-table receivables-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Period</th>
                  <th className="purchase-detail-lines-table__num">Orders</th>
                  <th className="purchase-detail-lines-table__num">Amount</th>
                  <th>Paid</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batchRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="receivables-empty-cell">
                      No {settlementKind} settlement batches yet.
                    </td>
                  </tr>
                ) : (
                  batchRows.map((batch) => (
                    <tr key={`${batch._type}-${batch.batchId}`}>
                      <td>#{batch.batchId}</td>
                      <td>
                        <span className={`receivables-status receivables-status--${statusTone(batch.status)}`}>
                          {batch.status || '—'}
                        </span>
                      </td>
                      <td>
                        {(batch.periodStart || '—')}
                        {' → '}
                        {(batch.periodEnd || '—')}
                      </td>
                      <td className="purchase-detail-lines-table__num">{batch.orderCount ?? 0}</td>
                      <td className="purchase-detail-lines-table__num">
                        {formatSettlementAmount(batch.totalAmount)}
                      </td>
                      <td>{batch.paidDate || '—'}</td>
                      <td>
                        <div className="receivables-row-actions">
                          <button
                            type="button"
                            className="purchase-btn purchase-btn--secondary"
                            onClick={() => openBatchDetail(batch.batchId, batch._type)}
                          >
                            View
                          </button>
                          {String(batch.status).toUpperCase() === 'PENDING' && (
                            <>
                              <button
                                type="button"
                                className="purchase-btn purchase-btn--primary"
                                onClick={() => openMarkPaid(batch)}
                                disabled={actionBusy}
                              >
                                Mark paid
                              </button>
                              <button
                                type="button"
                                className="purchase-btn purchase-btn--secondary"
                                onClick={() => handleCancelBatch(batch)}
                                disabled={actionBusy}
                              >
                                Cancel
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {batchDetail && (
        <div className="receivables-modal-backdrop" role="presentation" onClick={() => setBatchDetail(null)}>
          <div
            className="receivables-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Settlement batch detail"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="receivables-modal-header">
              <h2>
                Batch #{batchDetail.batchId}
                {' '}
                <span className={`receivables-status receivables-status--${statusTone(batchDetail.status)}`}>
                  {batchDetail.status}
                </span>
              </h2>
              <button type="button" className="purchase-btn purchase-btn--secondary" onClick={() => setBatchDetail(null)}>
                Close
              </button>
            </div>
            <p className="receivables-modal-meta">
              Amount {formatSettlementAmount(batchDetail.totalAmount)}
              {' · '}
              Type {batchDetail._type}
              {batchDetail.transactionId ? ` · Txn ${batchDetail.transactionId}` : ''}
            </p>
            <div className="purchase-detail-lines-table-wrap">
              <table className="purchase-detail-lines-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Detail</th>
                    <th className="purchase-detail-lines-table__num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(batchDetail.lines || []).length === 0 ? (
                    <tr>
                      <td colSpan={3} className="receivables-empty-cell">No lines</td>
                    </tr>
                  ) : (
                    (batchDetail.lines || []).map((line, idx) => (
                      <tr key={line.usageId ?? line.paymentId ?? idx}>
                        <td>#{line.orderId}</td>
                        <td>
                          {line.customerName || '—'}
                          {line.creditsUsed != null ? ` · ${line.creditsUsed} credits` : ''}
                          {line.paymentMethod ? ` · ${line.paymentMethod}` : ''}
                        </td>
                        <td className="purchase-detail-lines-table__num">
                          {formatSettlementAmount(
                            line.restaurantSettlementAmount ?? line.payableAmount
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {markPaidForm.batchId != null && (
        <div className="receivables-modal-backdrop" role="presentation">
          <div className="receivables-modal receivables-modal--sm" role="dialog" aria-modal="true">
            <h2>Mark batch #{markPaidForm.batchId} paid</h2>
            <label className="receivables-form-field">
              <span>Transaction ID / UTR</span>
              <input
                type="text"
                value={markPaidForm.transactionId}
                onChange={(e) => setMarkPaidForm((p) => ({ ...p, transactionId: e.target.value }))}
                placeholder="Optional"
              />
            </label>
            <label className="receivables-form-field">
              <span>Bank RRN</span>
              <input
                type="text"
                value={markPaidForm.bankRrn}
                onChange={(e) => setMarkPaidForm((p) => ({ ...p, bankRrn: e.target.value }))}
                placeholder="Optional"
              />
            </label>
            <label className="receivables-form-field">
              <span>Payment method</span>
              <input
                type="text"
                value={markPaidForm.paymentMethod}
                onChange={(e) => setMarkPaidForm((p) => ({ ...p, paymentMethod: e.target.value }))}
              />
            </label>
            <div className="receivables-row-actions">
              <button
                type="button"
                className="purchase-btn purchase-btn--secondary"
                onClick={() => setMarkPaidForm((p) => ({ ...p, batchId: null }))}
                disabled={actionBusy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="purchase-btn purchase-btn--primary"
                onClick={submitMarkPaid}
                disabled={actionBusy}
              >
                Confirm paid
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Receivables;
